import React from 'react';

import { cssMap as cssMapCompiled } from '@compiled/react';

import Lozenge, { type SemanticColor } from '@atlaskit/lozenge';
import { Anchor, Box, Text } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';
import {
	type ActionKeys,
	STORE_RECOMMENDATION_ACTION_KEY,
} from '@atlassian/conversation-assistant-message-actions-api/constants';
import type {
	ActionRendererFor,
	ActionRendererKeyToPayload,
	ActionWith,
} from '@atlassian/conversation-assistant-message-actions-api/types';
import { type MessageAction } from '@atlassian/conversation-assistant-service-api/types/message-actions';

type StoreRecommendationItem =
	ActionRendererKeyToPayload[typeof ActionKeys.StoreRecommendationCard]['items'][number];

const recommendationStyles = cssMapCompiled({
	list: {
		display: 'flex',
		flexDirection: 'column',
		gap: token('space.100'),
		width: '100%',
	},
	card: {
		display: 'flex',
		alignItems: 'flex-start',
		justifyContent: 'space-between',
		gap: token('space.100'),
		width: '100%',
		paddingBlock: token('space.100'),
		paddingInline: token('space.150'),
		backgroundColor: token('color.background.neutral.subtle'),
		borderRadius: token('radius.small'),
		borderWidth: token('border.width'),
		borderStyle: 'solid',
		borderColor: token('color.border'),
		boxSizing: 'border-box',
		textDecoration: 'none',
		color: token('color.text'),
	},
	title: {
		overflow: 'hidden',
		textOverflow: 'ellipsis',
		whiteSpace: 'nowrap',
	},
	content: {
		display: 'flex',
		flexDirection: 'column',
		gap: token('space.050'),
		flex: 1,
	},
	description: {
		whiteSpace: 'normal',
		overflowWrap: 'anywhere',
		color: token('color.text.subtle'),
	},
});

function getRecommendationTypeLabel(type: StoreRecommendationItem['type']): string {
	switch (type) {
		case 'solution':
			return 'SOLUTION';
		case 'agent':
			return 'AGENT';
		default:
			return 'APP';
	}
}

function getRecommendationLozengeAppearance(type: StoreRecommendationItem['type']): SemanticColor {
	switch (type) {
		case 'solution':
			return 'neutral';
		case 'agent':
			return 'discovery';
		default:
			return 'information';
	}
}

export const filterStoreRecommendationActions = (
	actions: MessageAction[],
): ActionWith<ActionRendererKeyToPayload[typeof ActionKeys.StoreRecommendationCard]>[] => {
	const filteredActions = actions.filter(assertIsStoreRecommendationAction);
	return filteredActions as ActionWith<
		ActionRendererKeyToPayload[typeof ActionKeys.StoreRecommendationCard]
	>[];
};

const assertIsStoreRecommendationAction = (action: MessageAction): MessageAction => {
	if (action.key !== STORE_RECOMMENDATION_ACTION_KEY) {
		throw new Error('Invalid action key for StoreRecommendationAction');
	}
	return action;
};

export const StoreRecommendationAction = ({
	actions,
}: ActionRendererFor<typeof ActionKeys.StoreRecommendationCard>): React.JSX.Element | null => {
	const firstAction = actions[0];
	if (!firstAction || !firstAction.data || !Array.isArray(firstAction.data.items)) {
		return null;
	}

	const recommendations = firstAction.data.items;

	if (recommendations.length === 0) {
		return null;
	}

	return (
		<Box xcss={recommendationStyles.list}>
			{recommendations.map((recommendation, index) => {
				const key = recommendation.id || `${STORE_RECOMMENDATION_ACTION_KEY}-${index}`;
				const typeLabel = getRecommendationTypeLabel(recommendation.type);
				const appearance = getRecommendationLozengeAppearance(recommendation.type);

				if (recommendation.href) {
					return (
						<Anchor
							key={key}
							href={recommendation.href}
							target="_blank"
							rel="noopener noreferrer"
							xcss={recommendationStyles.card}
						>
							<Box xcss={recommendationStyles.content}>
								<Box xcss={recommendationStyles.title}>
									<Text>{recommendation.name}</Text>
								</Box>
								{recommendation.description ? (
									<Box xcss={recommendationStyles.description}>
										<Text>{recommendation.description}</Text>
									</Box>
								) : null}
							</Box>
							<Lozenge appearance={appearance}>{typeLabel}</Lozenge>
						</Anchor>
					);
				}

				return (
					<Box key={key} xcss={recommendationStyles.card}>
						<Box xcss={recommendationStyles.content}>
							<Box xcss={recommendationStyles.title}>
								<Text>{recommendation.name}</Text>
							</Box>
							{recommendation.description ? (
								<Box xcss={recommendationStyles.description}>
									<Text>{recommendation.description}</Text>
								</Box>
							) : null}
						</Box>
						<Lozenge appearance={appearance}>{typeLabel}</Lozenge>
					</Box>
				);
			})}
		</Box>
	);
};
